import Lang from '@/modules/core/app/services/Lang';
import { Company } from '@/modules/company/models/Company';
import { SelfServeBillingPlanForm } from '@/modules/estate/models/SelfServeBillingPlanForm';
import { AuthUser } from '@/modules/auth/models/AuthUser';
import { PredefinedJurisdiction } from '@/modules/predefined/enums/PredefinedJurisdiction';
import { PartnershipInvitationType } from '@/modules/partnershipinvitation/constants/PartnershipInvitationType';
import { VaultPlanType } from '@/modules/estate/constants/VaultPlanType';
import { ProductKey } from '@/modules/estate/enums/ProductKey';

export class SolutionsBillingPlanForm extends SelfServeBillingPlanForm {
  authUser!: AuthUser;

  constructor(model: Partial<SolutionsBillingPlanForm> = {}) {
    super(model);

    this.authUser = model.authUser as AuthUser;
  }

  setAsStandardPlan(
    isRecommended: boolean = false,
    isROC: boolean = true,
    partnerCompany?: Company
  ): this {
    super.setAsStandardPlan(isRecommended, isROC, partnerCompany);

    this.subTitle = Lang.t('billingplan.unguided');

    this.upsellDescription = Lang.t('standardplan.create-will-ideal');
    if (this.authUser?.permission?.canAccessReferQuebecWitnessedWillPlans && !isROC) {
      this.upsellDescription = Lang.t('standardplan.create-will-ideal-quebec');
    }

    this.upsellTitle = Lang.t('basicplan.in-this-plan');
    this.inclusions = this.getStandardPlanInclusions(isROC);
    this.exclusions = this.getStandardPlanExclusions(isROC);

    return this;
  }

  setAsBasicPlan(
    isRecommended: boolean = false,
    isROC: boolean = true,
    partnerCompany?: Company
  ): this {
    super.setAsBasicPlan(isRecommended, isROC, partnerCompany);

    this.subTitle = Lang.t('billingplan.unguided');

    this.upsellDescription = Lang.t('basicplan.create-your-will-and-poa');
    this.upsellTitle = Lang.t('basicplan.everything-in-will');
    if (this.authUser?.permission?.canAccessReferQuebecWitnessedWillPlans && !isROC) {
      this.upsellDescription = Lang.t('basicplan.create-your-will-and-poa-quebec');
    }

    this.inclusions = this.getBasicPlanInclusions(isROC);
    this.exclusions = this.getBasicPlanExclusions(isROC);
    this.includedUpsells = [];
    this.excludedUpsells = [];

    return this;
  }

  setAsCompletePlan(
    isClient: boolean = false,
    isRecommended: boolean = false,
    discountPrice: number | undefined = undefined,
    isROC: boolean = true
  ): this {
    super.setAsCompletePlan(isClient, isRecommended, discountPrice, isROC);

    this.subTitle = Lang.t('billingplan.guided');

    this.upsellDescription = Lang.t('completeplan.create-an-optimized-estate');
    if (this.authUser?.permission?.canAccessReferQuebecWitnessedWillPlans && !isROC) {
      this.upsellDescription = Lang.t('completeplan.create-an-optimized-estate-quebec');
    }

    this.includedUpsells = this.getIncludedCompletePlanUpsells(isROC);

    return this;
  }

  setAsProfessionalPlan(
    isClient: boolean = false,
    isRecommended: boolean = false,
    discountPrice: number | undefined = undefined,
    isROC: boolean | undefined = true
  ): this {
    super.setAsProfessionalPlan(isClient, isRecommended, discountPrice, isROC);

    this.subTitle = Lang.t('billingplan.guided');

    this.upsellDescription = Lang.t('professionalplan.get-support-from-multi');
    if (this.authUser?.permission?.canAccessReferQuebecWitnessedWillPlans && !isROC) {
      this.upsellDescription = Lang.t('professionalplan.get-support-from-multi-quebec');
    }

    return this;
  }

  setAsDigitalVault(isClient: boolean = false, isRecommended: boolean = false): this {
    this.name = Lang.t('billingplan.digital-vault');
    this.subTitle = '';
    this.productKey = ProductKey.INDIVIDUAL_DIGITAL_VAULT_PLAN;
    this.upsellDescription = Lang.t('billingplan.digital-vault-upsell-description');
    this.upsellTitle = '';
    this.type = VaultPlanType.DIGITAL_VAULT.key;
    this.isRecommended = isRecommended;
    this.isClient = isClient;
    this.inclusions = [
      Lang.t('billingplan.digital-vault-store-your-existing-will'),
      Lang.t('billingplan.digital-vault-store-other-important-documents'),
      Lang.t('billingplan.digital-vault-keep-track-of-assets-and-liabilities')
    ];
    this.exclusions = [Lang.t('billingplan.digital-vault-generate-a-will-or-poa')];
    this.needs = [
      Lang.t('billingplan.digital-vault-dont-need-new-will-or-poa'),
      Lang.t(
        'billingplan.digital-vault-want-to-store-documents-asset-information-and-contact-information'
      )
    ];

    return this;
  }

  isQuebecEnabled(isROC: boolean = true): boolean {
    return !isROC && (this.authUser?.permission?.canAccessReferQuebecWitnessedWillPlans || false);
  }

  isAvailableForJurisdiction(jurisdictionId: number): boolean {
    switch (jurisdictionId) {
      case PredefinedJurisdiction.QUEBEC.id:
        if (!this.authUser?.permission?.canAccessReferQuebecWitnessedWillPlans) {
          return !(this.isStandardPlan || this.isBasicPlan);
        }
        break;
    }

    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getStandardPlanInclusions(isROC: boolean = true): string[] {
    return [
      this.isQuebecEnabled(isROC)
        ? Lang.t('billingplan.create-your-will-quebec')
        : Lang.t('billingplan.create-your-will'),
      this.isQuebecEnabled(isROC) ? null : Lang.t('billingplan.create-your-poa'),
      Lang.t('billingplan.online-portal-access')
    ].filter(Boolean);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getStandardPlanExclusions(isROC: boolean = true): string[] {
    return [
      Lang.t('billingplan.no-support'),
      Lang.t('billingplan.no-add-on-service'),
      Lang.t('billingplan.not-available-couples')
    ].filter(Boolean);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getBasicPlanInclusions(isROC: boolean = true): string[] {
    return [
      Lang.t('billingplan.visualize-distribution-plan'),
      Lang.t('billingplan.chat-assistance-customer-support'),
      Lang.t('billingplan.support-meeting-to-review-documents')
    ].filter(Boolean);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getBasicPlanExclusions(isROC: boolean = true): string[] {
    return [Lang.t('billingplan.no-add-on-service')].filter(Boolean);
  }

  getIncludedCompletePlanUpsells(isROC: boolean = true): string[] {
    return [
      this.isQuebecEnabled(isROC) ? Lang.t('billingplan.create-your-will-quebec') : null,
      this.isQuebecEnabled(isROC)
        ? Lang.t('billingplan.create-notarized-protection-mandate')
        : null,
      ...SelfServeBillingPlanForm.getIncludedCompletePlanUpsells(isROC)
    ].filter(Boolean);
  }

  loadPlanPrices(
    jurisdictionId?: number,
    companyId?: number,
    partnershipInvitationType?: PartnershipInvitationType
  ): Promise<void> {
    if (!this.isAvailableForJurisdiction(jurisdictionId as number)) {
      return Promise.resolve();
    }

    return super.loadPlanPrices(jurisdictionId, companyId, partnershipInvitationType);
  }
}
